import { Column } from "primereact/column";
import {
  DataTable,
  DataTableSelectionSingleChangeEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { useState } from "react";
import { useDebouncedUsersQuery } from "../hooks/useDebouncedUsersQuery";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { User } from "../models/users";
import {
  useAddUsersEventMutation,
  useGetEventByIdQuery,
} from "../store/services/events";
import toast from "react-hot-toast";
import { Dropdown } from "primereact/dropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { isUserRestrictedByBan } from "../utils/functions/users";
import { calculateRequiredSpots } from "../utils/functions/event-capacity";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "../utils/functions/api-errors";

const styles = `
  .banned-row {
    background-color: #ffebee !important;
  }
`;

interface SelectUsersProps {
  mode?: "user" | "fictional";
  eventId: number;
  handleClose: () => void;
}

export const SelectUsers = ({
  mode,
  eventId,
  handleClose,
}: SelectUsersProps) => {
  const [userEmail, setUserEmail] = useState<string>();
  const [userFirstName, setUserFirstName] = useState<string>();
  const [userLastName, setUserLastName] = useState<string>();
  const [usersSelected, setUsersSelected] = useState<User[]>([]);
  const [selectedGuests, setSelectedGuests] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;

  // Reset to page 1 whenever filters change
  const handleEmailChange = (value: string | undefined) => {
    setUserEmail(value);
    setCurrentPage(1);
  };
  const handleFirstNameChange = (value: string | undefined) => {
    setUserFirstName(value);
    setCurrentPage(1);
  };
  const handleLastNameChange = (value: string | undefined) => {
    setUserLastName(value);
    setCurrentPage(1);
  };

  const {
    data: usesrData,
    pagination,
    isLoading,
  } = useDebouncedUsersQuery({
    email: userEmail,
    firstName: userFirstName,
    lastName: userLastName,
    userTypeId: mode === "fictional" ? 3 : 2,
    limit: String(PAGE_SIZE),
    page: currentPage,
  });

  const {
    data: eventData,
    refetch: refetchEventData,
  } = useGetEventByIdQuery(eventId);

  const [addUsersEvent, { isLoading: isLoadingUsers }] =
    useAddUsersEventMutation();

  const eligibleUsersToAdd = usersSelected.filter(
    (user) => !isUserRestrictedByBan(user)
  );
  const blockedUsersCount = usersSelected.length - eligibleUsersToAdd.length;
  const guestsPerUser = mode === "user" ? selectedGuests : 0;
  const requiredSpots = calculateRequiredSpots(
    eligibleUsersToAdd.length,
    guestsPerUser
  );
  const availableSpots = eventData?.available;
  const hasInsufficientSpots =
    availableSpots !== undefined && requiredSpots > availableSpots;

  const visibleUsers = (usesrData || []).filter(
    (user) => !isUserRestrictedByBan(user)
  );
  const excludedUsersCount = (usesrData?.length || 0) - visibleUsers.length;

  const submitEnrollment = async () => {
    try {
      await addUsersEvent({
        id: eventId,
        usersIds: eligibleUsersToAdd.map((item) => item.id),
        guests: guestsPerUser,
      }).unwrap();
      toast.success("Usuarios agregados al evento exitosamente!");
      handleClose();
    } catch (error) {
      const status = getApiErrorStatus(error);
      const message = getApiErrorMessage(error);

      if (status === 409 || message?.toLowerCase().includes("cupos")) {
        toast.error(message || "No hay cupos disponibles para completar la inscripción");
        return;
      }

      toast.error(message || "No se pudieron agregar los usuarios al evento");
    }
  };

  const handleAdd = async () => {
    if (!eligibleUsersToAdd.length) {
      toast.error(
        "No hay usuarios elegibles para inscripción (usuarios restringidos no permitidos)"
      );
      return;
    }

    if (hasInsufficientSpots) {
      toast.error(
        `No hay cupos disponibles. Cupos requeridos: ${requiredSpots}, restantes: ${availableSpots}`
      );
      return;
    }

    const { data: freshEventData } = await refetchEventData();
    const freshAvailable = freshEventData?.available;
    const freshRequiredSpots = calculateRequiredSpots(
      eligibleUsersToAdd.length,
      guestsPerUser
    );

    if (
      freshAvailable !== undefined &&
      freshRequiredSpots > freshAvailable
    ) {
      toast.error(
        `No hay cupos disponibles. Cupos requeridos: ${freshRequiredSpots}, restantes: ${freshAvailable}`
      );
      return;
    }

    if (blockedUsersCount > 0) {
      toast.error(
        `Se bloquearon ${blockedUsersCount} usuario(s) con restricción de acceso`
      );
    }

    confirmDialog({
      header: "Confirmar inscripción",
      message: (
        <div className="flex flex-column gap-2">
          <span>
            Vas a inscribir <b>{eligibleUsersToAdd.length}</b> usuario(s)
            {mode === "user" && guestsPerUser > 0 && (
              <>
                {" "}
                con <b>{guestsPerUser}</b> invitado(s) por persona
              </>
            )}
            .
          </span>
          <span>
            Cupos requeridos: <b>{freshRequiredSpots}</b>
          </span>
          <span>
            Cupos disponibles:{" "}
            <b>{freshAvailable ?? "—"}</b>
          </span>
          <span>¿Deseás confirmar la inscripción?</span>
        </div>
      ) as unknown as string,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Confirmar",
      rejectLabel: "Cancelar",
      accept: submitEnrollment,
      // PrimeReact renderiza Cancelar antes que el primario; el footer propio
      // invierte el orden (SYSTEM_DESIGN §6.3 / INC-04).
      footer: ({
        accept,
        reject,
        acceptLabel,
        rejectLabel,
        acceptClassName,
        rejectClassName,
      }) => (
        <>
          <Button
            label={acceptLabel}
            className={acceptClassName}
            onClick={accept}
            autoFocus
          />
          <Button
            label={rejectLabel}
            className={rejectClassName}
            onClick={reject}
          />
        </>
      ),
    });
  };

  const header = (
    <div className="grid mt-4">
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="email"
            name="email"
            onChange={(e) => handleEmailChange(e.currentTarget.value)}
            value={userEmail}
          />
          <label htmlFor="email">Email</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => handleEmailChange("")}
        />
      </div>
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="lastName"
            name="lastName"
            onChange={(e) => handleLastNameChange(e.currentTarget.value)}
            value={userLastName}
          />
          <label htmlFor="lastName">Apellido</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => handleLastNameChange("")}
        />
      </div>
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="firstName"
            name="firstName"
            onChange={(e) => handleFirstNameChange(e.currentTarget.value)}
            value={userFirstName}
          />
          <label htmlFor="firstName">Nombre</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => handleFirstNameChange("")}
        />
      </div>
    </div>
  );

  return (
    <>
      <ConfirmDialog />
      <style>{styles}</style>
      <div className="flex justify-content-between align-items-center mb-2">
        <h1 className="text-lg  m-0 p-0 ">
          Seleccione los usuarios que desea agregar al evento
        </h1>
        <Button
          icon="pi pi-plus"
          label="Agregar"
          onClick={handleAdd}
          disabled={
            !eligibleUsersToAdd.length ||
            selectedGuests === undefined ||
            hasInsufficientSpots
          }
          loading={isLoadingUsers}
        />
      </div>
      {eligibleUsersToAdd.length > 0 && availableSpots !== undefined && (
        <div
          className={`mb-2 font-medium ${
            hasInsufficientSpots ? "text-red-600" : "text-700"
          }`}
        >
          Cupos requeridos: {requiredSpots} / Disponibles: {availableSpots}
        </div>
      )}
      {excludedUsersCount > 0 && (
        <div className="mb-2 text-orange-600 font-medium">
          Se excluyeron automáticamente {excludedUsersCount} usuario(s) con
          restricción de acceso.
        </div>
      )}
      {usersSelected.length > 0 && mode === "user" && (
        <div className="col-6 md:col-3 my-3">
          <span className="p-float-label">
            <Dropdown
              className={`min-w-full`}
              id="guests"
              name="guests"
              onChange={(e) => setSelectedGuests(e.target.value)}
              optionLabel="name"
              emptyFilterMessage="No se encontraron resultados"
              emptyMessage="No hay invitados"
              optionValue="value"
              options={
                eventData
                  ? Array.from({ length: eventData.guests + 1 }, (_, i) => ({
                      name: `${i}`,
                      value: i,
                    }))
                  : []
              }
              value={selectedGuests}
            />
            <label htmlFor="guest">Invitados por persona</label>
          </span>
        </div>
      )}
      <DataTable
        value={visibleUsers}
        loading={isLoading}
        header={header}
        selection={usersSelected}
        selectionMode={undefined}
        onSelectionChange={({
          value,
        }: DataTableSelectionSingleChangeEvent<DataTableValueArray>) => {
          if (Array.isArray(value)) {
            setUsersSelected(value ? [...(value as User[])] : []);
          }
        }}
        lazy
        paginator
        rows={PAGE_SIZE}
        totalRecords={pagination?.total ?? 0}
        first={(currentPage - 1) * PAGE_SIZE}
        onPage={(e) => {
          const newPage = Math.floor(e.first / PAGE_SIZE) + 1;
          setCurrentPage(newPage);
        }}
        // rowsPerPageOptions={[25, 50, 100]}
      >
        <Column
          selectionMode={"multiple"}
          headerStyle={{ width: "3rem" }}
        ></Column>
        <Column field="email" header="Email"></Column>
        <Column field="lastName" header="Apellido"></Column>
        <Column field="firstName" header="Nombre"></Column>
        <Column
          field="banned"
          header="Ban"
          body={(rowData: User) => (
            <i
              className={`pi ${
                isUserRestrictedByBan(rowData)
                  ? "pi-ban text-red-500"
                  : "pi-check text-green-500"
              }`}
              style={{ fontSize: "1.2rem" }}
              title={
                isUserRestrictedByBan(rowData)
                  ? "Usuario restringido"
                  : "Usuario activo"
              }
            />
          )}
        ></Column>
      </DataTable>
    </>
  );
};
